/**
 * LeafletRouteMap — Shared reusable map component.
 *
 * Renders origin/destination markers, optional intermediate waypoints, deadhead lines,
 * toll route alternatives, single polylines, and OSRM/GraphHopper auto-routing.
 * Supports light/dark theme via `isDark` prop.
 *
 * Used by: agentic-hub (LoadMapSection) and load-hub (PrivateLoadMapSection).
 */

import { FC, useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import axios from 'axios'
import { RoutingRoutesRequest, RoutingRoutesResponse } from '@numeo/types-dispatcher-ui'
import { decodePolyline } from './mapPolyline'
import { pinSvg } from './mapPins'
import { ROUTE_PIN_DESTINATION_COLOR, ROUTE_PIN_ORIGIN_COLOR } from 'constants/mapPins'

// ─── Public Types ────────────────────────────────────────────────────────────

export interface LatLng {
    lat: number
    lng: number
}

export interface Waypoint extends LatLng {
    label: string
}

export interface RouteAlternative {
    routeId: string
    polyline: string
    distance: { meters: number; miles: number; text: string }
    duration: { seconds: number; minutes: number; text: string }
    summary: string
    isRecommended: boolean
    tollCost?: { amount: number; currency: string; count: number }
    tollLocations?: Array<{ lat: number; lng: number; name: string; cost?: { amount: number; currency: string } }>
}

interface LeafletRouteMapProps {
    origin: LatLng
    destination: LatLng
    /** Intermediate stops between origin and destination */
    waypoints?: Waypoint[]
    /** User's searched origin (deadhead start point) — rendered as marker A when different from origin */
    userSearchedOrigin?: LatLng | null
    polyline?: string
    routes?: RouteAlternative[]
    selectedRouteIndex?: number
    height?: string
    width?: string
    isDark?: boolean
    /** Called once after route fetch completes with distances in meters (null when not computed) */
    onDistanceCalculated?: (tripMeters: number | null, deadheadMeters: number | null) => void
}

// ─── Constants ───────────────────────────────────────────────────────────────

const ROUTE_COLOR_SELECTED = '#3B82F6'
const ROUTE_COLOR_UNSELECTED = '#9CA3AF'

const TILE_SOURCES = {
    light: { url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap contributors' },
    dark: { url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', attribution: '&copy; OpenStreetMap contributors &copy; CARTO' },
} as const

const useGraphHopper = process.env.REACT_APP_USE_GRAPHHOPPER_ROUTING === 'true'

// ─── GraphHopper Helper ──────────────────────────────────────────────────────

async function fetchGraphHopperRoutes(
    origin: LatLng,
    destination: LatLng,
    searchOrigin: LatLng | null | undefined,
    signal: AbortSignal
): Promise<RoutingRoutesResponse | null> {
    try {
        const payload: RoutingRoutesRequest = { origin, destination }
        if (searchOrigin) payload.searchOrigin = searchOrigin
        const response = await axios.post<{ success: boolean; data: RoutingRoutesResponse }>('/routing/routes', payload, { signal })
        return response.data.success ? response.data.data : null
    } catch {
        return null
    }
}

// ─── Marker Icons ────────────────────────────────────────────────────────────

function makePinIcon(color: string, letter: string, size: [number, number] = [24, 34]): L.DivIcon {
    const [w, h] = size
    return L.divIcon({
        className: 'leaflet-custom-icon',
        html: pinSvg(color, letter, w, h),
        iconSize: [w, h],
        iconAnchor: [w / 2, h],
    })
}

const markerAIcon = makePinIcon('#3B82F6', 'A')
const markerBIcon = makePinIcon('#3B82F6', 'B')
const originIcon = makePinIcon(ROUTE_PIN_ORIGIN_COLOR, 'A', [28, 40])
const destinationIcon = makePinIcon(ROUTE_PIN_DESTINATION_COLOR, 'B', [28, 40])

function makeWaypointIcon(label: string): L.DivIcon {
    return makePinIcon('#F97316', label, [22, 31])
}

// ─── Component ───────────────────────────────────────────────────────────────

export const LeafletRouteMap: FC<LeafletRouteMapProps> = ({
    origin,
    destination,
    waypoints = [],
    userSearchedOrigin,
    polyline,
    routes,
    selectedRouteIndex = 0,
    height = '100%',
    width = '100%',
    isDark = false,
    onDistanceCalculated,
}) => {
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<L.Map | null>(null)
    const routeLayersRef = useRef<L.Polyline[]>([])
    const requestIdRef = useRef(0)
    const [osrmRoute, setOsrmRoute] = useState<Array<[number, number]> | null>(null)
    const [deadheadOsrmRoute, setDeadheadOsrmRoute] = useState<Array<[number, number]> | null>(null)

    const waypointsKey = waypoints.map((w) => `${w.lat},${w.lng}`).join('|')
    const tileSource = isDark ? TILE_SOURCES.dark : TILE_SOURCES.light

    // Fetch routes when no toll routes or polyline provided
    useEffect(() => {
        if (polyline || (routes && routes.length > 0)) return

        const reqId = ++requestIdRef.current
        const controller = new AbortController()
        const isStale = () => requestIdRef.current !== reqId || controller.signal.aborted

        const fetchWithFallback = async () => {
            if (useGraphHopper && waypoints.length === 0) {
                const result = await fetchGraphHopperRoutes(origin, destination, userSearchedOrigin, controller.signal)
                if (isStale()) return
                if (result) {
                    setOsrmRoute(decodePolyline(result.mainRoute.points))
                    setDeadheadOsrmRoute(result.deadheadRoute ? decodePolyline(result.deadheadRoute.points) : null)
                    onDistanceCalculated?.(result.mainRoute.distance, result.deadheadRoute?.distance ?? null)
                    return
                }
            }

            const mainStops: LatLng[] = [origin, ...waypoints, destination]
            const coords = mainStops.map((s) => `${s.lng},${s.lat}`).join(';')
            const url = `https://router.project-osrm.org/route/v1/driving/${coords}?overview=full&geometries=polyline`
            let tripMeters: number | null = null
            let deadheadMeters: number | null = null

            try {
                const response = await fetch(url, { signal: controller.signal })
                const data = await response.json()
                if (data.code === 'Ok' && data.routes?.length > 0) {
                    setOsrmRoute(decodePolyline(data.routes[0].geometry))
                    tripMeters = data.routes[0].distance ?? null
                }
            } catch {
                setOsrmRoute(null)
            }

            if (isStale()) return

            if (userSearchedOrigin) {
                const dhCoords = `${userSearchedOrigin.lng},${userSearchedOrigin.lat};${origin.lng},${origin.lat}`
                try {
                    const dhResponse = await fetch(`https://router.project-osrm.org/route/v1/driving/${dhCoords}?overview=full&geometries=polyline`, {
                        signal: controller.signal,
                    })
                    const dhData = await dhResponse.json()
                    if (dhData.code === 'Ok' && dhData.routes?.length > 0) {
                        setDeadheadOsrmRoute(decodePolyline(dhData.routes[0].geometry))
                        deadheadMeters = dhData.routes[0].distance ?? null
                    } else {
                        setDeadheadOsrmRoute(null)
                    }
                } catch {
                    setDeadheadOsrmRoute(null)
                }
            } else {
                setDeadheadOsrmRoute(null)
            }

            if (isStale()) return
            onDistanceCalculated?.(tripMeters, deadheadMeters)
        }

        void fetchWithFallback()
        return () => {
            controller.abort()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [origin.lat, origin.lng, destination.lat, destination.lng, waypointsKey, polyline, routes, userSearchedOrigin?.lat, userSearchedOrigin?.lng])

    // Render map with markers, routes, polylines
    useEffect(() => {
        if (!mapContainerRef.current) return

        if (mapRef.current) {
            try {
                mapRef.current.stop()
                mapRef.current.remove()
            } catch {
                /* ignore */
            }
            mapRef.current = null
        }
        routeLayersRef.current = []

        // Safety: clear Leaflet's internal container ID
        const container = mapContainerRef.current
        const leafletContainer = container as HTMLElement & { _leaflet_id?: number }
        if (leafletContainer._leaflet_id) {
            delete leafletContainer._leaflet_id
        }

        const map = L.map(container, {
            zoomControl: true,
            attributionControl: false,
            dragging: true,
            scrollWheelZoom: true,
            doubleClickZoom: true,
        })

        L.tileLayer(tileSource.url, { maxZoom: 19, attribution: tileSource.attribution }).addTo(map)
        mapRef.current = map

        const hasDeadhead = !!userSearchedOrigin
        const allPositions: Array<[number, number]> = []

        // Place markers
        if (hasDeadhead) {
            L.marker([userSearchedOrigin.lat, userSearchedOrigin.lng], { icon: markerAIcon }).addTo(map)
            allPositions.push([userSearchedOrigin.lat, userSearchedOrigin.lng])

            L.marker([origin.lat, origin.lng], { icon: markerBIcon }).addTo(map)
            allPositions.push([origin.lat, origin.lng])

            waypoints.forEach((wp, i) => {
                const letter = String.fromCharCode('C'.charCodeAt(0) + i)
                L.marker([wp.lat, wp.lng], { icon: makePinIcon('#3B82F6', letter) }).addTo(map)
                allPositions.push([wp.lat, wp.lng])
            })

            const lastLetter = String.fromCharCode('C'.charCodeAt(0) + waypoints.length)
            L.marker([destination.lat, destination.lng], { icon: makePinIcon('#3B82F6', lastLetter) }).addTo(map)
            allPositions.push([destination.lat, destination.lng])
        } else if (waypoints.length > 0) {
            L.marker([origin.lat, origin.lng], { icon: originIcon }).addTo(map)
            allPositions.push([origin.lat, origin.lng])

            waypoints.forEach((wp) => {
                L.marker([wp.lat, wp.lng], { icon: makeWaypointIcon(wp.label) }).addTo(map)
                allPositions.push([wp.lat, wp.lng])
            })

            L.marker([destination.lat, destination.lng], { icon: destinationIcon }).addTo(map)
            allPositions.push([destination.lat, destination.lng])
        } else {
            L.marker([origin.lat, origin.lng], { icon: originIcon }).addTo(map)
            L.marker([destination.lat, destination.lng], { icon: destinationIcon }).addTo(map)
            allPositions.push([origin.lat, origin.lng], [destination.lat, destination.lng])
        }

        // Deadhead line
        if (hasDeadhead) {
            const dhPoints: Array<[number, number]> =
                deadheadOsrmRoute && deadheadOsrmRoute.length > 0
                    ? deadheadOsrmRoute
                    : [
                          [userSearchedOrigin.lat, userSearchedOrigin.lng],
                          [origin.lat, origin.lng],
                      ]
            L.polyline(dhPoints, { color: '#F97316', weight: 4, opacity: 1 }).addTo(map)
        }

        const fitToAll = () => map.fitBounds(L.latLngBounds(allPositions), { padding: [30, 30] })

        // Priority: 1. Toll routes  2. Provided polyline  3. OSRM  4. Straight dashed line
        if (routes && routes.length > 0) {
            const sortedRoutes = routes
                .map((route, index) => ({ route, index }))
                .sort((a, b) => {
                    if (a.index === selectedRouteIndex) return 1
                    if (b.index === selectedRouteIndex) return -1
                    return 0
                })

            sortedRoutes.forEach(({ route, index }) => {
                try {
                    const points = decodePolyline(route.polyline)
                    const isSelected = index === selectedRouteIndex
                    routeLayersRef.current.push(
                        L.polyline(points, {
                            color: isSelected ? ROUTE_COLOR_SELECTED : ROUTE_COLOR_UNSELECTED,
                            weight: isSelected ? 5 : 3,
                            opacity: isSelected ? 1 : 0.6,
                        }).addTo(map)
                    )
                } catch {
                    /* skip invalid polylines */
                }
            })
            fitToAll()
        } else if (polyline) {
            try {
                const points = decodePolyline(polyline)
                routeLayersRef.current.push(L.polyline(points, { color: ROUTE_COLOR_SELECTED, weight: 4, opacity: 1 }).addTo(map))
            } catch {
                /* ignore */
            }
            fitToAll()
        } else if (osrmRoute && osrmRoute.length > 0) {
            routeLayersRef.current.push(L.polyline(osrmRoute, { color: ROUTE_COLOR_SELECTED, weight: 4, opacity: 1 }).addTo(map))
            fitToAll()
        } else {
            routeLayersRef.current.push(
                L.polyline(
                    [
                        [origin.lat, origin.lng],
                        [destination.lat, destination.lng],
                    ],
                    { color: ROUTE_COLOR_SELECTED, weight: 3, opacity: 0.7, dashArray: '10, 10' }
                ).addTo(map)
            )
            fitToAll()
        }

        return () => {
            if (mapRef.current) {
                try {
                    mapRef.current.stop()
                    mapRef.current.remove()
                } catch {
                    /* ignore */
                }
                mapRef.current = null
            }
            routeLayersRef.current = []
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        origin.lat,
        origin.lng,
        destination.lat,
        destination.lng,
        waypointsKey,
        userSearchedOrigin?.lat,
        userSearchedOrigin?.lng,
        polyline,
        osrmRoute,
        deadheadOsrmRoute,
        routes,
        selectedRouteIndex,
        tileSource.url,
    ])

    // Invalidate map size when container resizes
    useEffect(() => {
        const el = mapContainerRef.current
        if (!el) return
        const observer = new ResizeObserver(() => mapRef.current?.invalidateSize())
        observer.observe(el)
        return () => observer.disconnect()
    }, [])

    return <div ref={mapContainerRef} style={{ width, height, borderRadius: '6px' }} className="leaflet-route-map" />
}
